/**
 * Transport abstraction. The game only talks to `Room`s; how peers find each
 * other (Trystero over public signaling, a local relay, BroadcastChannel or an
 * in-memory hub for tests) is an implementation detail.
 */

export type Channel = 'reliable' | 'unreliable';

/** Reliable messages are JSON-serialisable objects; unreliable ones are raw bytes. */
export type JsonMessage = { t: string } & Record<string, unknown>;

export interface Room {
  readonly id: string;
  readonly selfId: string;
  peers(): string[];
  /** Reliable, ordered JSON message to one peer, a list, or everyone. */
  send(msg: JsonMessage, to?: string | readonly string[]): void;
  /** Unreliable binary message (snapshots). Falls back to reliable where unsupported. */
  sendBinary(bytes: Uint8Array, to?: string | readonly string[]): void;
  onMessage(cb: (from: string, msg: JsonMessage) => void): () => void;
  onBinary(cb: (from: string, bytes: Uint8Array) => void): () => void;
  onPeerJoin(cb: (id: string) => void): () => void;
  onPeerLeave(cb: (id: string) => void): () => void;
  leave(): Promise<void>;
}

export interface Transport {
  readonly kind: 'trystero' | 'ws-relay' | 'broadcast' | 'loopback';
  /** This peer's id (valid once `init()` resolved). */
  readonly selfId: string;
  /** Loads the transport (dynamic imports etc.). */
  init(): Promise<void>;
  join(roomId: string, opts?: { password?: string }): Promise<Room>;
}

/**
 * Listener set shared by room implementations. Events emitted before anyone
 * subscribed (peers greet a room the moment it opens, before `join()` has even
 * returned to the caller) are held and replayed, a microtask after the first
 * subscription, to everyone subscribed by then.
 */
export class Listeners<T extends unknown[]> {
  private set = new Set<(...args: T) => void>();
  private pending: T[] | null = [];

  add(fn: (...args: T) => void): () => void {
    this.set.add(fn);
    const held = this.pending;
    if (held) {
      this.pending = null;
      if (held.length) queueMicrotask(() => held.forEach((args) => this.dispatch(args)));
    }
    return () => this.set.delete(fn);
  }

  emit(...args: T): void {
    if (this.pending) {
      if (this.pending.length < 256) this.pending.push(args);
      return;
    }
    this.dispatch(args);
  }

  private dispatch(args: T): void {
    for (const fn of [...this.set]) {
      try {
        fn(...args);
      } catch (e) {
        console.error('net listener failed', e);
      }
    }
  }

  clear(): void {
    this.set.clear();
  }
}

/** A string field from an untrusted message (anything else becomes `fallback`). */
export function asText(v: unknown, fallback = '', max = 64): string {
  return typeof v === 'string' ? v.slice(0, max) : fallback;
}

export function randomId(len = 16): string {
  const bytes = new Uint8Array(len);
  crypto.getRandomValues(bytes);
  return Array.from(bytes, (b) => (b % 36).toString(36)).join('');
}
